/**
 * Hook for fetching company update counts for badge display
 *
 * Performance: Returns a Map for O(1) lookups instead of O(n) array.find()
 * With 50 companies, this reduces comparisons from 2500 to 50 per render.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Materiality } from '../types';
import { getAccessToken } from '../services/api';

interface CompanyUpdateInfo {
  id: string;
  unread_count: number;
  highest_materiality: Materiality | null;
  latest_event: {
    title: string;
    event_type: string;
    published_at: string;
  } | null;
}

interface CompanyUpdatesResponse {
  companies: CompanyUpdateInfo[];
  since: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

async function fetchCompanyUpdates(sinceHours: number = 168): Promise<CompanyUpdatesResponse> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const response = await fetch(
    `${API_BASE_URL}/api/companies/updates?since_hours=${sinceHours}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch company updates');
  }

  return response.json();
}

export function useCompanyUpdates(sinceHours: number = 168) {
  const query = useQuery({
    queryKey: ['companyUpdates', sinceHours],
    queryFn: () => fetchCompanyUpdates(sinceHours),
    refetchInterval: 60000, // Poll every minute
    staleTime: 30000, // Consider data stale after 30 seconds
    retry: 2, // Retry a couple times for transient failures
  });

  // PERF: Pre-compute Map for O(1) lookups in render loops
  // Avoids O(n) array.find() called per company in LeftNav
  const updatesMap = useMemo(() => {
    if (!query.data?.companies) return new Map<string, CompanyUpdateInfo>();
    return new Map(query.data.companies.map((c) => [c.id, c]));
  }, [query.data?.companies]);

  return { ...query, updatesMap };
}

/**
 * Get update info for a specific company from the updates response
 */
export function getCompanyUpdateInfo(
  updates: CompanyUpdatesResponse | undefined,
  companyId: string
): CompanyUpdateInfo | null {
  if (!updates) return null;
  return updates.companies.find((c) => c.id === companyId) || null;
}

export default useCompanyUpdates;
